// Unified permissions admin — a users × services matrix.
//
// This is the single place to grant/revoke per-user access to every gated
// microservice (the SERVICE_REGISTRY). It self-gates to operator super_admin
// and writes through the setUserPermission callable (Admin SDK, server-side
// validated). Over time it absorbs the role tabs in LogsAdmin/RecordingsAdmin.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useHistory } from 'react-router-dom';

import { getCurrentUser, readUser } from '../firebase';
import { resolveOperatorRank } from '../pm/constants';
import { SERVICE_REGISTRY, permLevel } from '../permissions/registry';
import {
  PermissionUser,
  setUserPermission,
  subscribePermissionUsers,
} from '../permissions/firebase';

import '../pm/theme.css';

type Access = { kind: 'loading' } | { kind: 'denied' } | { kind: 'ok' };

const card: React.CSSProperties = {
  background: 'var(--rr-paper, #fff)',
  border: '1px solid var(--pm-border, #d8d4cc)',
  borderRadius: 'var(--pm-radius-lg, 12px)',
  padding: '16px 18px',
  marginTop: 16,
};
const cellSelect: React.CSSProperties = {
  padding: '5px 8px',
  border: '1px solid var(--pm-border, #d8d4cc)',
  borderRadius: 8,
  fontSize: 13,
  background: 'var(--rr-paper, #fff)',
};
const thTd: React.CSSProperties = { padding: '9px 12px', whiteSpace: 'nowrap' };
const NONE = ''; // sentinel select value = no grant

const Permissions: React.FC = () => {
  const history = useHistory();
  const [access, setAccess] = useState<Access>({ kind: 'loading' });
  const [users, setUsers] = useState<PermissionUser[] | null>(null);
  const [filter, setFilter] = useState('');
  const [busyCell, setBusyCell] = useState<string | null>(null);
  const [msg, setMsg] = useState('');

  // Self-gate: operator super_admin only.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u: any = await getCurrentUser();
        if (!u) return !cancelled && setAccess({ kind: 'denied' });
        const data = await readUser(u.uid);
        const rank = resolveOperatorRank(data && data.restricted);
        if (!cancelled) setAccess({ kind: rank === 'super_admin' ? 'ok' : 'denied' });
      } catch {
        if (!cancelled) setAccess({ kind: 'denied' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Live user list once access is granted.
  useEffect(() => {
    if (access.kind !== 'ok') return;
    const unsub = subscribePermissionUsers(setUsers);
    return unsub;
  }, [access.kind]);

  const changeCell = useCallback(
    async (email: string, service: string, value: string) => {
      const level = value === NONE ? null : value;
      setBusyCell(`${email}:${service}`);
      setMsg('');
      try {
        await setUserPermission(email, service, level);
        setMsg(`${email}: ${service} → ${level ?? '(none)'}`);
      } catch (err: any) {
        setMsg(err?.message || 'Failed to update permission.');
      } finally {
        setBusyCell(null);
      }
    },
    [],
  );

  const shown = useMemo(() => {
    if (!users) return [];
    const q = filter.trim().toLowerCase();
    return q ? users.filter((u) => u.email.toLowerCase().includes(q)) : users;
  }, [users, filter]);

  if (access.kind === 'loading') {
    return (
      <IonPage>
        <IonContent fullscreen>
          <div className="pm-app">
            <div className="pm-page" style={{ padding: '40px 28px' }}>
              <IonSpinner />
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (access.kind === 'denied') {
    return (
      <IonPage>
        <IonContent fullscreen>
          <div className="pm-app">
            <div className="pm-page" style={{ maxWidth: 720, padding: '40px 28px' }}>
              <div className="pm-header pm-header-titles">
                <h1>Permissions</h1>
                <p>
                  You don’t have access to permission management. This page is limited to
                  operator super admins.
                </p>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="pm-app">
          <div className="pm-page" style={{ maxWidth: 1200, padding: '24px 28px' }}>
            <div
              className="pm-header"
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}
            >
              <div className="pm-header-titles">
                <h1>Permissions</h1>
                <p>Grant per-user access to each microservice. Changes take effect immediately.</p>
              </div>
              <button
                type="button"
                className="pm-btn"
                style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
                onClick={() => history.push('/Operator')}
              >
                ← Operator hub
              </button>
            </div>

            <div style={card}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  style={{
                    padding: '8px 10px',
                    border: '1px solid var(--pm-border, #d8d4cc)',
                    borderRadius: 8,
                    fontSize: 14,
                    flex: '1 1 260px',
                  }}
                  placeholder="Filter by email…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <span style={{ fontSize: 12, color: '#888' }}>
                  {users ? `${shown.length} of ${users.length} users` : 'Loading…'}
                </span>
              </div>
              {msg && <div style={{ marginTop: 8, fontSize: 13 }}>{msg}</div>}

              <div style={{ overflowX: 'auto', marginTop: 12 }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', background: 'var(--rr-stone-100, #f4f2ee)' }}>
                      <th style={{ ...thTd, position: 'sticky', left: 0, background: 'var(--rr-stone-100, #f4f2ee)' }}>
                        User
                      </th>
                      {SERVICE_REGISTRY.map((s) => (
                        <th key={s.key} style={thTd} title={s.description}>
                          {s.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {!users && (
                      <tr>
                        <td style={thTd} colSpan={SERVICE_REGISTRY.length + 1}>
                          <IonSpinner />
                        </td>
                      </tr>
                    )}
                    {shown.map((u) => (
                      <tr key={u.uid} style={{ borderTop: '1px solid var(--pm-border, #eee)' }}>
                        <td style={{ ...thTd, position: 'sticky', left: 0, background: 'var(--rr-paper, #fff)' }}>
                          {u.email}
                        </td>
                        {SERVICE_REGISTRY.map((s) => {
                          const current = permLevel(u.restricted, s.key) ?? NONE;
                          const cellKey = `${u.email}:${s.key}`;
                          return (
                            <td key={s.key} style={thTd}>
                              <select
                                style={cellSelect}
                                value={current}
                                disabled={busyCell === cellKey}
                                onChange={(e) => changeCell(u.email, s.key, e.target.value)}
                              >
                                <option value={NONE}>—</option>
                                {s.levels.map((lvl) => (
                                  <option key={lvl} value={lvl}>
                                    {lvl}
                                  </option>
                                ))}
                              </select>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p style={{ marginTop: 12, fontSize: 12, color: '#888' }}>
                “—” means no access. Granting <strong>operator</strong> also sets the legacy staff
                flag so the person gets the operator app shell. Recordings/intake-logs still honor
                their legacy roles too during the migration.
              </p>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Permissions;
